import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, Loader2, Clock, ShieldAlert, Ban } from "lucide-react";

export const Logo = ({ dark = false, className = "" }) => (
  <span className={`inline-flex items-center gap-2 ${className}`} data-testid="brand-logo">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={`w-7 h-7 ${dark ? "text-white" : "text-gray-900"}`} aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M7 12h2l2-3 2 6 2-3h2" stroke="#DC2626" strokeWidth="2.2" />
    </svg>
    <span className={`font-display font-black text-lg tracking-tight ${dark ? "text-white" : "text-gray-900"}`}>
      Code<span className="text-red-600">Red</span>
    </span>
  </span>
);

const TONES = {
  healthy: ["text-green-700 bg-green-50 border-green-200", CheckCircle2],
  warning: ["text-amber-700 bg-amber-50 border-amber-200", AlertTriangle],
  critical: ["text-red-700 bg-red-50 border-red-200", XCircle],
  unknown: ["text-gray-600 bg-gray-100 border-gray-200", HelpCircle],
  running: ["text-blue-700 bg-blue-50 border-blue-200", Loader2],
  waiting: ["text-amber-700 bg-amber-50 border-amber-200", Clock],
  blocked: ["text-gray-700 bg-gray-100 border-gray-300", Ban],
  security: ["text-red-700 bg-red-50 border-red-200", ShieldAlert],
};

export const Pill = ({ tone = "unknown", children, testid }) => {
  const [cls, Icon] = TONES[tone] || TONES.unknown;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 border px-2 py-0.5 text-xs font-medium rounded-sm whitespace-nowrap ${cls}`}>
      <Icon className={`w-3.5 h-3.5 ${tone === "running" ? "animate-spin" : ""}`} aria-hidden="true" />
      {children}
    </span>
  );
};

const CONN = { online: ["healthy", "Online"], offline: ["critical", "Offline"], pending: ["unknown", "Awaiting first check-in"], revoked: ["blocked", "Revoked"], pending_approval: ["waiting", "Pending approval"] };
export const ConnBadge = ({ status, testid }) => {
  const [t, l] = CONN[status] || ["unknown", status];
  return <Pill tone={t} testid={testid}>{l}</Pill>;
};

const SEV = { critical: "critical", high: "critical", medium: "warning", low: "unknown" };
export const SevBadge = ({ severity, testid }) => <Pill tone={SEV[severity]} testid={testid}>{severity}</Pill>;

const IST = {
  new: "warning", investigating: "running", diagnosed: "waiting", awaiting_approval: "waiting",
  remediation_scheduled: "waiting", remediation_running: "running", resolved: "healthy", escalated: "critical", closed: "unknown",
};
export const IncidentStatus = ({ status, testid }) => <Pill tone={IST[status]} testid={testid}>{status?.replaceAll("_", " ")}</Pill>;

const TST = {
  queued: ["waiting", "Queued"], dispatched: ["running", "Running on device"], succeeded_verified: ["healthy", "Verified success"],
  verification_failed: ["critical", "Verification failed"], failed: ["critical", "Failed"], timed_out: ["critical", "Timed out"],
  cancelled: ["blocked", "Cancelled"], expired: ["unknown", "Expired"],
};
export const TaskStatus = ({ status, testid }) => {
  const [t, l] = TST[status] || ["unknown", status];
  return <Pill tone={t} testid={testid}>{l}</Pill>;
};

const RISK = { 1: ["L1 Read-only", "bg-gray-100 text-gray-700 border-gray-300"], 2: ["L2 Low-risk", "bg-green-50 text-green-800 border-green-200"],
  3: ["L3 Moderate", "bg-amber-50 text-amber-800 border-amber-200"], 4: ["L4 High-risk", "bg-red-600 text-white border-red-700"] };
export const RiskBadge = ({ level }) => {
  const [l, c] = RISK[level] || RISK[1];
  return <span className={`inline-flex border px-2 py-0.5 text-xs font-mono font-medium rounded-sm ${c}`} data-testid={`risk-badge-l${level}`}>{l}</span>;
};

export const PageHeader = ({ title, crumbs = [], children, testid = "page-header" }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8" data-testid={testid}>
    <div>
      {crumbs.length > 0 && <div className="text-xs text-gray-500 mb-1">{crumbs.join(" / ")}</div>}
      <h1 className="text-2xl md:text-3xl font-black text-gray-900">{title}</h1>
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Stat = ({ label, value, sub, tone, testid, icon: Icon }) => (
  <div className="card-flat p-5 lift" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</span>
      {Icon && <Icon className={`w-4 h-4 ${tone === "bad" ? "text-red-600" : tone === "warn" ? "text-amber-600" : tone === "good" ? "text-green-600" : "text-gray-400"}`} />}
    </div>
    <div className="mt-2 text-3xl font-display font-black text-gray-900">{value ?? "—"}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

export const Empty = ({ title, text, children, testid = "empty-state" }) => (
  <div className="card-flat p-10 text-center border-dashed" data-testid={testid}>
    <div className="font-display font-bold text-gray-900">{title}</div>
    {text && <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">{text}</p>}
    {children && <div className="mt-4 flex justify-center gap-2">{children}</div>}
  </div>
);

export const Loading = () => <div className="loader-bar w-full my-6" data-testid="loading-bar" />;
export const ErrorBox = ({ error }) => error ? <div className="border border-red-200 bg-red-50 text-red-700 text-sm p-3 rounded-sm" data-testid="error-box">{error}</div> : null;
